import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, effect, inject, input } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, shareReplay, switchMap, takeUntil } from 'rxjs/operators';
import { DictionaryService } from 'src/app/data/dictionary.service';
import { WorkerService } from 'src/app/data/worker.service';
import { ApiResult } from 'src/app/models/ApiResult';
import { DictionaryItem } from 'src/app/models/dictionary-item';
import { ConfirmDialogComponent, ConfirmDialogData } from 'src/app/shared/messages/confirm-dialog/confirm-dialog.component';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { AssignEmployerObjectDataSource } from './assign-employer-object.datasource';

type AddEmployerObjectForm = FormGroup<{
  employerObject: FormControl<DictionaryItem | string | null>;
}>;

@Component({
  selector: 'app-assign-employer-object',
  templateUrl: './assign-employer-object.component.html',
  styleUrls: ['./assign-employer-object.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AssignEmployerObjectComponent implements OnInit, OnDestroy {
  private readonly formBuilder = inject(FormBuilder);
  private readonly dictionaryService = inject(DictionaryService);
  private readonly workerService = inject(WorkerService);
  private readonly snackbarService = inject(SnackBarService);
  private readonly dialog = inject(MatDialog);
  private readonly cdr = inject(ChangeDetectorRef);

  private readonly timeBetweenInput = 300;
  private readonly pageSize = 50;
  private readonly duplicateErrorCode = 'WorkerAlreadyAssignedToEmployerObject';

  readonly internalWorkerId = input<number>(0);
  isMutating = false;

  readonly searchControl = new FormControl<string>('', { nonNullable: true });

  readonly addEmployerObjectForm: AddEmployerObjectForm;
  readonly employerObjectControl: FormControl<DictionaryItem | string | null>;
  readonly dataSource: AssignEmployerObjectDataSource;

  readonly addEmployerObjects$: Observable<DictionaryItem[]>;
  readonly assignedEmployerObjects$: Observable<DictionaryItem[]>;
  private readonly destroy$ = new Subject<void>();

  get canAddEmployerObject(): boolean {
    return this.isDictionaryItem(this.employerObjectControl.value) && !this.isMutating;
  }

  constructor() {
    this.dataSource = new AssignEmployerObjectDataSource(this.workerService);

    this.addEmployerObjectForm = this.formBuilder.group({
      employerObject: this.formBuilder.control<DictionaryItem | string | null>(null),
    });

    this.employerObjectControl = this.addEmployerObjectForm.controls.employerObject;

    this.addEmployerObjects$ = this.employerObjectControl.valueChanges.pipe(
      debounceTime(this.timeBetweenInput),
      distinctUntilChanged(),
      switchMap((value: DictionaryItem | string | null) => {
        const query = this.toQuery(value);
        if (!query) {
          return of([]);
        }
        return this.dictionaryService.getInternalWorkerEmployerObjects(this.internalWorkerId(), query, this.pageSize, 1);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.assignedEmployerObjects$ = this.dataSource.rows$;

    effect(() => {
      const workerId = this.internalWorkerId();
      if (workerId === 0) {
        return;
      }

      this.searchControl.setValue('', { emitEvent: false });
      this.dataSource.initialize(workerId, this.pageSize);
    });
  }

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(this.timeBetweenInput), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((term) => {
        this.dataSource.setFilter(term);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.dataSource.disconnect();
  }

  clearSearch(): void {
    this.searchControl.setValue('');
  }

  onEmployerObjectSelected(item: DictionaryItem): void {
    if (!item) {
      return;
    }

    this.employerObjectControl.setValue(item, { emitEvent: false });
    this.employerObjectControl.markAsTouched();
  }

  addSelectedEmployerObject(): void {
    const value = this.employerObjectControl.value;

    if (!this.isDictionaryItem(value) || this.isMutating) {
      this.employerObjectControl.markAsTouched();
      return;
    }

    this.isMutating = true;
    this.cdr.markForCheck();
    this.workerService
      .addInternalWorkerEmployerObject(this.internalWorkerId(), value.Id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isMutating = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (res: ApiResult<any>) => {
          if (res.IsSuccess) {
            this.snackbarService.openSuccessSnackBar('UF-AssignEmployerObjectAdded');
            this.employerObjectControl.setValue(null, { emitEvent: false });
            this.dataSource.refresh();
            return;
          }

          if (res.Error === this.duplicateErrorCode) {
            this.snackbarService.openErrorSnackBar('UF-AssignEmployerObjectDuplicate');
          } else {
            this.snackbarService.openErrorSnackBar(res.Error ?? 'SomethingWentWrong');
          }
        },
        error: () => {
          this.snackbarService.openErrorSnackBar('SomethingWentWrong');
        }
      });
  }

  confirmDeleteEmployerObject(item: DictionaryItem): void {
    if (!item?.Id || this.isMutating) {
      return;
    }

    this.isMutating = true;
    this.cdr.markForCheck();

    this.dialog
      .open(ConfirmDialogComponent, {
        data: new ConfirmDialogData('UF-AssignEmployerObjectDeleteConfirmTitle', 'UF-AssignEmployerObjectDeleteConfirmMessage')
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed: boolean) => {
        if (!confirmed) {
          this.isMutating = false;
          this.cdr.markForCheck();
          return;
        }

        this.workerService
          .removeInternalWorkerEmployerObject(this.internalWorkerId(), item.Id)
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => {
              this.isMutating = false;
              this.cdr.markForCheck();
            }),
          )
          .subscribe({
            next: (res: ApiResult<any>) => {
              if (res.IsSuccess) {
                this.snackbarService.openSuccessSnackBar('UF-AssignEmployerObjectRemoved');
                this.dataSource.refresh();
              } else {
                this.snackbarService.openErrorSnackBar(res.Error ?? 'SomethingWentWrong');
              }
            },
            error: () => {
              this.snackbarService.openErrorSnackBar('SomethingWentWrong');
            }
          });
      });
  }

  onScrolledIndexChange(index: number): void {
    const remainingItems = this.dataSource.rows.length - index;
    const threshold = this.pageSize / 2;
    if (remainingItems <= threshold) {
      this.dataSource.fetchNextPage();
    }
  }

  displayValue(value: DictionaryItem): string {
    return value?.Name ?? '';
  }

  trackByEmployerObjectId(_: number, item: DictionaryItem): number {
    return item.Id;
  }

  private toQuery(value: DictionaryItem | string | null): string {
    const query = typeof value === 'string' ? value : value?.Name;
    const normalized = (query ?? '').toString().trim();
    return normalized.length >= 1 ? normalized : '';
  }

  private isDictionaryItem(value: unknown): value is DictionaryItem {
    return !!value && typeof value === 'object' && !!(value as DictionaryItem).Id;
  }
}